// withI18nLoader.js
'use strict';

import { getLangFromPath, normalizeLang } from '../resource/langPath.js';
import { DEFAULT_LANGUAGE } from '../resource/i18nCommon.js';
import { getI18nInstance } from '../resource/i18nServer.js';

/**
 * Higher-order function that wraps a loader with i18n initialization.
 * This avoids repeating i18n setup logic in every loader.
 *
 * @param {Function} loader - The original loader function
 * @param {string[]} namespaces - Translation namespaces to load
 * @returns {Function} Wrapped loader with i18nInstance injected
 *
 */
export const withI18nLoader = (loader, namespaces = []) => {
  return async args => {
    const { request } = args;

    const url = new URL(request.url);
    const langFromPath = getLangFromPath(url.pathname);
    const language = langFromPath
      ? normalizeLang(langFromPath)
      : DEFAULT_LANGUAGE;

    // Server: create/clone instance for SSR; Client: reuse existing global instance
    const isServer = typeof window === 'undefined';
    const i18nInstance = isServer
      ? await getI18nInstance({ language })
      : (await import('../resource/i18n.js')).default;

    if (namespaces.length > 0) {
      await i18nInstance.loadNamespaces(namespaces);
    }

    return loader({
      ...args,
      i18nInstance,
      language,
    });
  };
};

export default withI18nLoader;
